import React from 'react';
import { useAppContext } from 'src/components/app-context';
import type { Product, RecommendationsQuery } from 'src/types';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { ProductsSection } from 'src/components/products-section';
import { filterCategories } from '../../filter-categories';
import { usePersonalizedQuery } from 'src/hooks/use-personalized-query';
import query from './query.graphql';

type Props = {
  negativeMargin?: boolean;
};

const PersonalizedProductRecommendations: React.FC<Props> = props => {
  const { t } = useAppContext();

  const result = usePersonalizedQuery<RecommendationsQuery>({
    query,
    queryKey: ['personalized-recommendations'],
  });

  const products = result.data?.personalizedRecommendations?.products;

  if (
    result.isError ||
    !(products && products.nodes) ||
    !products.nodes.length ||
    products.fallbackToDefault
  ) {
    return null;
  }

  const productsItems = filterCategories(products.nodes as Product[]);

  const trackHeaderClick = (data: { link: string }) => {
    trackInternalNavigationClick({
      scope: InternalNavigationScope.PERSONALIZED_RECOMMENDATIONS_HEADER,
      nextPage: data.link,
    });
  };

  const trackProductCardClick = (data: { index: number; link: string }) => {
    trackInternalNavigationClick({
      scope: InternalNavigationScope.PERSONALIZED_RECOMMENDATIONS_PRODUCT_CARD,
      nextPage: data.link,
      index: data.index,
    });
  };

  return (
    <ProductsSection
      products={productsItems}
      title={t('landing', 'personalized-products.title')}
      subTitle={t('landing', 'personalized-recommendations.subtitle')}
      linkPath="/personalization"
      linkTitle={t('account', 'menu.settings')}
      shouldLazyLoadImages
      dataTestType="product-recommendations"
      onHeaderLinkClick={trackHeaderClick}
      onProductCardClick={trackProductCardClick}
      negativeMargin={props.negativeMargin}
    />
  );
};

export default PersonalizedProductRecommendations;
